import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { ArrowLeft, Download, BarChart3, TrendingUp, ShieldAlert, Brain } from "lucide-react";

function Bar({ label, value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-kb-ink min-w-[140px] truncate">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-kb-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="kb-mono text-xs text-kb-ink-muted w-8 text-right">{value}</span>
    </div>
  );
}

export default function SurveyAnalytics() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/surveys/${id}/analytics`).then((r) => setData(r.data));
  }, [id]);

  const downloadCsv = () => {
    const token = localStorage.getItem("kb_session_token");
    const url = `${API}/surveys/${id}/export`;
    // We use fetch with auth then blob to trigger download
    fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((res) => res.blob()).then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `survey_${id}.csv`;
      link.click();
    });
  };

  if (!data) return <div className="kb-mono text-xs text-kb-ink-muted">Loading analytics…</div>;
  const s = data.survey;

  return (
    <div data-testid="survey-analytics-page">
      <Link to="/dashboard/surveys" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SURVEYS
      </Link>

      <div className="mt-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SURVEY ANALYTICS · {s.research_type}</div>
          <h1 className="font-serif text-3xl text-kb-ink mt-1">{s.title}</h1>
        </div>
        <button onClick={downloadCsv} className="kb-btn-outline text-sm" data-testid="survey-export-csv-btn">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {/* Headline metrics */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
        <div className="bg-white p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Total responses</div>
          <div className="font-mono text-3xl text-kb-primary mt-2">{data.n_total}</div>
        </div>
        <div className="bg-white p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Clean (analysed)</div>
          <div className="font-mono text-3xl text-kb-primary mt-2">{data.n_clean}</div>
        </div>
        <div className="bg-white p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Cronbach α</div>
          <div className="font-mono text-3xl text-kb-primary mt-2">{data.reliability_cronbach_alpha ?? "—"}</div>
        </div>
        <div className="bg-white p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Median duration</div>
          <div className="font-mono text-3xl text-kb-primary mt-2">{data.duration_stats?.median_seconds ?? "—"}<span className="text-sm text-kb-ink-muted">s</span></div>
        </div>
      </div>

      {/* Auto summary */}
      <div className="mt-8 kb-card p-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
          <Brain className="w-3.5 h-3.5" /> AUTO SUMMARY
        </div>
        <ul className="mt-3 space-y-1.5 text-sm text-kb-ink">
          {data.summary.map((line, i) => <li key={i}>· {line}</li>)}
        </ul>
      </div>

      {/* Quality flags */}
      {Object.keys(data.flags).length > 0 && (
        <div className="mt-6 kb-card p-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" /> QUALITY FLAGS
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(data.flags).map(([k, v]) => (
              <span key={k} className="kb-mono text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm">
                {k.replace(/_/g, " ")} · {v}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Per-question */}
      <div className="mt-6 space-y-4">
        {data.questions.map((q) => (
          <div key={q.id} className="kb-card p-6" data-testid={`analytics-q-${q.id}`}>
            <div className="flex items-center justify-between">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{q.id} · {q.type}</div>
              <span className="kb-mono text-[10px] text-kb-primary">n = {q.count ?? 0}</span>
            </div>
            <div className="font-serif text-lg text-kb-ink mt-2">{q.text}</div>

            {(q.type === "rating" || q.type === "number" || q.type === "likert") && q.mean !== undefined && (
              <div className="mt-4 grid sm:grid-cols-5 gap-3 text-xs">
                <div><div className="kb-mono text-[10px] text-kb-ink-light">Mean</div><div className="font-mono text-kb-primary text-base">{q.mean}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">Median</div><div className="font-mono text-kb-primary text-base">{q.median}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">SD</div><div className="font-mono text-kb-primary text-base">{q.stdev}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">Min</div><div className="font-mono text-kb-primary text-base">{q.min}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">Max</div><div className="font-mono text-kb-primary text-base">{q.max}</div></div>
              </div>
            )}

            {q.frequency && Object.keys(q.frequency).length > 0 && (
              <div className="mt-4">
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">FREQUENCY</div>
                {(() => {
                  const max = Math.max(...Object.values(q.frequency));
                  return Object.entries(q.frequency).map(([k, v]) => <Bar key={k} label={k} value={v} max={max} />);
                })()}
              </div>
            )}

            {q.top_terms && q.top_terms.length > 0 && (
              <div className="mt-4">
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">TOP TERMS (qualitative coding)</div>
                <div className="flex flex-wrap gap-1.5">
                  {q.top_terms.map((t) => (
                    <span key={t.term} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">
                      {t.term} · {t.count}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {q.samples && q.samples.length > 0 && (
              <div className="mt-4">
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">SAMPLE RESPONSES</div>
                <ul className="space-y-2">
                  {q.samples.map((s, i) => (
                    <li key={i} className="text-sm text-kb-ink-muted border-l-2 border-slate-200 pl-3 line-clamp-3">{s}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {data.correlations.length > 0 && (
        <div className="mt-6 kb-card p-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> PEARSON CORRELATIONS
          </div>
          <table className="mt-4 w-full text-sm">
            <thead className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
              <tr><th className="text-left pb-2">A</th><th className="text-left pb-2">B</th><th className="text-right pb-2">r</th><th className="text-right pb-2">n</th></tr>
            </thead>
            <tbody>
              {data.correlations.map((c, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-2 text-kb-ink">{c.a}</td>
                  <td className="py-2 text-kb-ink">{c.b}</td>
                  <td className={`py-2 text-right font-mono ${Math.abs(c.pearson) > 0.5 ? "text-kb-primary font-bold" : "text-kb-ink-muted"}`}>{c.pearson}</td>
                  <td className="py-2 text-right kb-mono text-xs text-kb-ink-muted">{c.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-3">
            * Strong correlations (|r| &gt; 0.5) highlighted. For full PLS-SEM workflow, export CSV and use specialised tools.
          </p>
        </div>
      )}
    </div>
  );
}
